"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createUTCMatchingDate = createUTCMatchingDate;
exports.formatHourTimespan = formatHourTimespan;
exports.getDiffInHours = getDiffInHours;
exports.transformTimeToString = transformTimeToString;
exports.transformTimeToDecimal = transformTimeToDecimal;
exports.transformDecimalToTime = transformDecimalToTime;
exports.formatDate = formatDate;
exports.countryUses12HourFormat = countryUses12HourFormat;
exports.formatTimeByCountryIsoCode = formatTimeByCountryIsoCode;
exports.formatDateInTimezone = formatDateInTimezone;
exports.covertLocalDateToDepartureDate = covertLocalDateToDepartureDate;
exports.toUTCStartOfDay = toUTCStartOfDay;
exports.toUTCEndOfDay = toUTCEndOfDay;
exports.calculateNowInBookingTime = calculateNowInBookingTime;
exports.calculateDepartInFromBookingTime = calculateDepartInFromBookingTime;
const constants_1 = require("@daytrip/constants");
const date_fns_tz_1 = require("date-fns-tz");
const getAdjustedTimezone_1 = require("./helpers/getAdjustedTimezone");
const transformUTCtoZonedTime_1 = require("./transformers/transformUTCtoZonedTime");
/**
 * Performs timezone-safe date parsing so the dates match between UTC and any other timezone
 *
 * @param {(Date | string)} input - Date to parse
 * @return {Date}  {Date} - Parsed date
 */
function createUTCMatchingDate(input) {
    // Simple parse first
    const date = new Date(input);
    // Make it safer, consistent
    date.setUTCHours(0);
    // If the offset is negative, we need to shift the time back
    // so the dates match between UTC and any other timezone
    if (date.getTimezoneOffset() < 0) {
        const offset = date.getTimezoneOffset() * 60 * 1000;
        date.setTime(date.getTime() - offset);
    }
    // Finally, construct the date
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), 
    // We have offsets in minutes in some timezones
    // So we need to account for that
    date.getUTCMinutes());
}
function formatHourTimespan(timestampOne, timestampTwo, originTimezone) {
    const DATE_FORMAT = "hh:mm b";
    const adjustedTimezone = (0, getAdjustedTimezone_1.getAdjustedTimezone)(originTimezone);
    const formattedTimeOne = (0, date_fns_tz_1.formatInTimeZone)(timestampOne, adjustedTimezone, DATE_FORMAT);
    const formattedTimeTwo = (0, date_fns_tz_1.formatInTimeZone)(timestampTwo, adjustedTimezone, DATE_FORMAT);
    if (formattedTimeOne === formattedTimeTwo) {
        return formattedTimeOne;
    }
    if (timestampOne > timestampTwo) {
        return `${formattedTimeTwo} - ${formattedTimeOne}`;
    }
    return `${formattedTimeOne} - ${formattedTimeTwo}`;
}
/*
 * Comparing 2 dates and returning diff in hours
 * Diff will be negative if date1 > date2
 */
function getDiffInHours(date1, date2) {
    const dateObject1 = new Date(date1);
    const dateObject2 = new Date(date2);
    return (dateObject2.valueOf() - dateObject1.valueOf()) / 3600000;
}
function transformTimeToString(tmpDate, with24hoursFormat = false, isUtc = true, isWithPhase = true) {
    const date = new Date(tmpDate);
    if (isUtc) {
        date.setHours(date.getUTCHours());
        date.setMinutes(date.getUTCMinutes());
    }
    const formattedTime = (0, date_fns_tz_1.format)(date, isWithPhase ? "hh:mm b" : "hh:mm");
    return formattedTime + (with24hoursFormat ? ` (${(0, date_fns_tz_1.format)(date, "HH:mm")})` : "");
}
function transformTimeToDecimal(time) {
    if (!time.includes(":")) {
        throw new Error("Invalid time format. Expected a string in 'HH:MM' format.");
    }
    const [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
        throw new Error("Invalid time format. Hours and minutes should be numbers.");
    }
    const decimalTime = hours + minutes / 60;
    return parseFloat(decimalTime.toFixed(2));
}
function transformDecimalToTime(decimalTime) {
    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);
    return `${padZero(hours)}:${padZero(minutes)}`;
}
function padZero(num) {
    return num < 10 ? "0" + num : num.toString();
}
function formatDate(date, formatString, options) {
    if (options?.timeZone) {
        options.timeZone = (0, getAdjustedTimezone_1.getAdjustedTimezone)(options.timeZone);
    }
    return (0, date_fns_tz_1.format)(date, formatString, options);
}
function countryUses12HourFormat(countryCode) {
    return constants_1.COUNTRIES_USING_12_HOUR_FORMAT.includes(countryCode);
}
function formatTimeByCountryIsoCode(date, countryCode, options) {
    if (options?.timeZone) {
        options.timeZone = (0, getAdjustedTimezone_1.getAdjustedTimezone)(options.timeZone);
    }
    const timeFormat = countryUses12HourFormat(countryCode) ? "hh:mm a" : "HH:mm";
    return (0, date_fns_tz_1.format)(date, timeFormat, options);
}
function formatDateInTimezone(date, timezone, formatString, options) {
    const adjustedTimezone = (0, getAdjustedTimezone_1.getAdjustedTimezone)(timezone);
    return (0, date_fns_tz_1.formatInTimeZone)(date, adjustedTimezone, formatString, options);
}
function covertLocalDateToDepartureDate(localDate) {
    return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), localDate.getHours(), localDate.getMinutes(), localDate.getSeconds()));
}
function toUTCStartOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}
function toUTCEndOfDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
}
function calculateNowInBookingTime(originTimeZone) {
    const now = new Date();
    const nowUTC = new Date(now.getTime() - now.getTimezoneOffset() * constants_1.MINUTE_IN_MILLISECONDS);
    const nowShifted = (0, transformUTCtoZonedTime_1.transformUTCtoZonedTime)(nowUTC, originTimeZone);
    return nowShifted.getTime();
}
function calculateDepartInFromBookingTime(bookingTime, originTimeZone, units) {
    const nowShifted = calculateNowInBookingTime(originTimeZone);
    const duration = bookingTime.getTime() - nowShifted;
    switch (units) {
        case "hours":
            return duration / constants_1.HOUR_IN_MILLISECONDS;
        case "minutes":
            return duration / constants_1.MINUTE_IN_MILLISECONDS;
        case "seconds":
            return duration / 1000;
        case "ms":
        default:
            return duration;
    }
}
//# sourceMappingURL=date-utils.js.map